import { useCallback, useEffect, useState } from "react";
import { Button, Chip, Skeleton } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { userState } from "../../interface/MainInterface";
import { HttpMainApi } from "../../interface/main-api";
import {
  Book,
  categoryLabels,
  DEFAULT_BOOK_IMAGE,
  emptyBookHome,
  getSafeExternalUrl,
  isBookSection,
  normalizeBookHome,
} from "./book-home";

interface Props {
  userState: userState;
}

type LoadStatus = "loading" | "ready" | "error";

const mainApi = new HttpMainApi();

function BookCover({ book }: { book: Book }) {
  return (
    <img
      className="hp-book-detail-card__cover"
      src={book.image || DEFAULT_BOOK_IMAGE}
      alt={`${book.title} 표지`}
      onError={(event) => {
        event.currentTarget.onerror = null;
        event.currentTarget.src = DEFAULT_BOOK_IMAGE;
      }}
    />
  );
}

const MobileBookCategory = (_props: Props) => {
  const navigate = useNavigate();
  const { section: sectionParam } = useParams<{ section: string }>();
  const section = isBookSection(sectionParam) ? sectionParam : undefined;
  const [bookHome, setBookHome] = useState(emptyBookHome);
  const [status, setStatus] = useState<LoadStatus>("loading");
  const [error, setError] = useState("");

  const loadBooks = useCallback(async () => {
    if (!section) return;
    setStatus("loading");
    setError("");

    try {
      const response = await mainApi.get_book_home({});
      if (response.code !== "200") throw new Error("도서 목록을 불러오지 못했습니다.");
      setBookHome(normalizeBookHome(response.response?.result));
      setStatus("ready");
    } catch (loadError) {
      setBookHome(emptyBookHome());
      setStatus("error");
      setError(loadError instanceof Error ? loadError.message : "도서 목록을 불러오는 중 문제가 발생했습니다.");
    }
  }, [section]);

  useEffect(() => {
    void loadBooks();
  }, [loadBooks]);

  if (!section) return <Navigate replace to="/mobilebooktemplate" />;

  const books = bookHome.categories[section];
  const title = categoryLabels[section];

  return (
    <main className="hpoint-renewal hp-reservation-page hp-book-page hp-book-detail-page">
      <header className="hp-book-detail-page__header">
        <button className="hp-book-detail-page__back" type="button" onClick={() => navigate("/mobilebooktemplate")}>
          <ArrowBackRoundedIcon aria-hidden="true" />
          전체 도서
        </button>
        <span className="hp-book-category__eyebrow">작은 도서관</span>
        <h1 className="hp-book-detail-page__title">{title}</h1>
        <p className="hp-book-detail-page__description">관심 있는 책을 천천히 둘러보세요.</p>
        {status === "ready" ? <span className="hp-book-detail-page__count">{books.length}권</span> : null}
      </header>

      {status === "loading" ? (
        <div className="hp-book-detail-list hp-book-detail-list--skeleton" aria-label={`${title} 도서 불러오는 중`} role="status">
          {[0, 1, 2, 3].map((item) => <Skeleton key={item} variant="rounded" />)}
        </div>
      ) : null}

      {status === "error" ? (
        <div className="hp-empty-state hp-book-empty-state">
          <MenuBookRoundedIcon aria-hidden="true" />
          <p>{error}</p>
          <Button className="hp-button hp-button--secondary" onClick={() => void loadBooks()} startIcon={<RefreshRoundedIcon />}>
            다시 불러오기
          </Button>
        </div>
      ) : null}

      {status === "ready" && books.length ? (
        <div className="hp-book-detail-list" role="list" aria-label={`${title} 전체 도서 목록`}>
          {books.map((book, index) => {
            const safeUrl = getSafeExternalUrl(book.url);
            const content = (
              <>
                <div className="hp-book-detail-card__visual">
                  <span className="hp-book-detail-card__index" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
                  <BookCover book={book} />
                  {book.isBorrowed ? <span className="hp-book-borrowed-badge">대여중</span> : null}
                </div>
                <div className="hp-book-detail-card__content">
                  <div className="hp-book-detail-card__chips">
                    {book.tag ? <Chip className="hp-chip" label={book.tag} size="small" /> : null}
                    {book.badge ? <Chip className="hp-chip" label={book.badge} size="small" /> : null}
                  </div>
                  <h2>{book.title}</h2>
                  <p className="hp-book-detail-card__author">{book.author || "작가 정보 없음"}</p>
                  <p className="hp-book-detail-card__description">{book.maincopy || book.description || "작은 도서관에서 만날 수 있는 도서입니다."}</p>
                  {safeUrl ? <span className="hp-book-detail-card__link">도서 정보 <OpenInNewRoundedIcon aria-hidden="true" /></span> : null}
                </div>
              </>
            );

            return safeUrl ? (
              <a key={`${book.title}-${index}`} className="hp-book-detail-card" href={safeUrl} target="_blank" rel="noopener noreferrer" role="listitem" aria-label={`${book.title} 도서 정보 보기`}>
                {content}
              </a>
            ) : (
              <article key={`${book.title}-${index}`} className="hp-book-detail-card" role="listitem" aria-label={book.title}>
                {content}
              </article>
            );
          })}
        </div>
      ) : null}

      {status === "ready" && !books.length ? (
        <div className="hp-empty-state hp-book-empty-state hp-book-detail-page__empty">
          <MenuBookRoundedIcon aria-hidden="true" />
          <p>{title} 도서를 준비하고 있습니다.</p>
        </div>
      ) : null}
    </main>
  );
};

export default MobileBookCategory;
